import {Proposal, ProposalStatus} from "../types/Proposal";
import {getTypeLabel} from "../components/form/dropdown/ProposalTypeDropdown";

export type AnaliticsListing = Pick<Proposal, never> & {
    id?: string;
    source?: string | null;
    address?: string;
    lat?: number;
    lng?: number;
    type?: string;
    rooms?: string | number;
    renovationStatus?: string;
    price?: number;
    area?: number;
    proposalStatus?: string;
    createdAt?: number;
};

export type DistrictAnalitics = {
    city: string;
    district: string;
    listingCount: number;
    soldCount: number;
    demandScore: number;
    liquidityScore: number;
    demandRank: number;
    liquidityRank: number;
};

export type CityAssetAnalitics = {
    city: string;
    kinds: string[];
};

export type CityCapitalAnalitics = {
    city: string;
    listingCount: number;
    pricedCount: number;
    entryPrice: number;
    typicalPrice: number;
};

export type InvestorAnalitics = {
    currentCity: string | null;
    districts: DistrictAnalitics[];
    assetsByCity: CityAssetAnalitics[];
    capitalByCity: CityCapitalAnalitics[];
};

type KnownPlace = {
    label: string;
    cityTokens: string[];
    countryTokens: string[];
    lat: number;
    lng: number;
    radiusKm: number;
};

const KNOWN_PLACES: KnownPlace[] = [
    {
        label: "Batumi, Georgia",
        cityTokens: ["batumi", "батуми"],
        countryTokens: ["georgia", "грузия"],
        lat: 41.646,
        lng: 41.642,
        radiusKm: 80,
    },
    {
        label: "Minsk, Belarus",
        cityTokens: ["minsk", "минск", "мінск"],
        countryTokens: ["belarus", "беларусь"],
        lat: 53.9,
        lng: 27.567,
        radiusKm: 80,
    },
];

type NamedDistrict = {
    city: string;
    name: string;
    aliases: string[];
    lat: number;
    lng: number;
    radiusKm: number;
};

const NAMED_DISTRICTS: NamedDistrict[] = [
    {city: "Batumi, Georgia", name: "Old Boulevard", aliases: ["old boulevard"], lat: 41.651, lng: 41.639, radiusKm: 1.6},
    {city: "Batumi, Georgia", name: "Gonio", aliases: ["gonio", "гонио"], lat: 41.563, lng: 41.572, radiusKm: 4},
    {city: "Batumi, Georgia", name: "Adlia", aliases: ["adlia", "адлиа", "адлия"], lat: 41.616, lng: 41.600, radiusKm: 3},
    {city: "Batumi, Georgia", name: "Angisa", aliases: ["angisa", "ангиса"], lat: 41.625, lng: 41.628, radiusKm: 2},
    {city: "Batumi, Georgia", name: "Center", aliases: ["center", "centre", "boulevard"], lat: 41.646, lng: 41.642, radiusKm: 2},
    {city: "Minsk, Belarus", name: "Center", aliases: ["center", "centre", "центральный", "tsentralny", "central"], lat: 53.904, lng: 27.561, radiusKm: 2.4},
    {city: "Minsk, Belarus", name: "Sovetsky", aliases: ["sovetsky", "советский"], lat: 53.930, lng: 27.580, radiusKm: 2.6},
    {city: "Minsk, Belarus", name: "Pervomaysky", aliases: ["pervomaysky", "первомайский"], lat: 53.938, lng: 27.640, radiusKm: 3.6},
    {city: "Minsk, Belarus", name: "Partizansky", aliases: ["partizansky", "партизанский"], lat: 53.875, lng: 27.630, radiusKm: 3.2},
    {city: "Minsk, Belarus", name: "Zavodskoy", aliases: ["zavodskoy", "заводской"], lat: 53.842, lng: 27.628, radiusKm: 5},
    {city: "Minsk, Belarus", name: "Leninsky", aliases: ["leninsky", "ленинский"], lat: 53.858, lng: 27.575, radiusKm: 2.6},
    {city: "Minsk, Belarus", name: "Oktyabrsky", aliases: ["oktyabrsky", "октябрьский"], lat: 53.878, lng: 27.530, radiusKm: 2.6},
    {city: "Minsk, Belarus", name: "Moskovsky", aliases: ["moskovsky", "московский"], lat: 53.885, lng: 27.495, radiusKm: 5.5},
    {city: "Minsk, Belarus", name: "Frunzensky", aliases: ["frunzensky", "фрунзенский"], lat: 53.910, lng: 27.450, radiusKm: 4},
    {city: "Minsk, Belarus", name: "Borovlyany", aliases: ["borovlyany", "боровляны", "боровлянский"], lat: 53.990, lng: 27.675, radiusKm: 4},
    {city: "Minsk, Belarus", name: "Kopishche", aliases: ["kopishche", "kopische", "копище"], lat: 53.954, lng: 27.674, radiusKm: 2.6},
    {city: "Minsk, Belarus", name: "Zhdanovichi", aliases: ["zhdanovichi", "ждановичи"], lat: 53.943, lng: 27.425, radiusKm: 1.4},
    {city: "Minsk, Belarus", name: "Kolodishchi", aliases: ["kolodishchi", "колодищи", "колодищанский"], lat: 53.970, lng: 27.820, radiusKm: 4},
];

export function cityFromSource(source?: string | null): string {
    const raw = (source || "").trim();
    if (!raw) {
        return "Unknown city";
    }
    const lower = raw.toLowerCase();
    if (lower === "relapka") {
        return "Batumi, Georgia";
    }
    const parts = lower.split("_").filter(Boolean);
    if (parts.length >= 2) {
        const country = titleCase(parts[0]);
        const city = titleCase(parts[1]);
        return `${city}, ${country}`;
    }
    return titleCase(raw.replace(/_/g, " "));
}

export function cityFromListing(listing: AnaliticsListing): string {
    const source = (listing.source || "").trim();
    if (source) {
        return cityFromSource(source);
    }
    return cityFromAddress(listing.address) || cityFromCoords(listing.lat, listing.lng) || "Unknown city";
}

export function districtFromListing(listing: AnaliticsListing): string | null {
    const city = cityFromListing(listing);
    const fromAddress = districtFromAddress(listing.address, city);
    if (fromAddress) {
        return fromAddress;
    }
    return districtFromCoords(listing.lat, listing.lng, city);
}

export function buildInvestorAnalitics(listings: AnaliticsListing[]): InvestorAnalitics {
    const usable = listings.filter((row) => row && row.proposalStatus !== "DELETED");

    const districtMap = new Map<string, {
        city: string;
        district: string;
        listingCount: number;
        soldCount: number;
    }>();
    const kindsByCity = new Map<string, Set<string>>();
    const pricesByCity = new Map<string, number[]>();
    const countByCity = new Map<string, number>();

    for (const row of usable) {
        const city = cityFromListing(row);
        countByCity.set(city, (countByCity.get(city) || 0) + 1);

        const kind = assetKindFromListing(row);
        if (kind) {
            if (!kindsByCity.has(city)) kindsByCity.set(city, new Set());
            kindsByCity.get(city)!.add(kind);
        }

        const price = Number(row.price);
        if (isFinite(price) && price > 0) {
            if (!pricesByCity.has(city)) pricesByCity.set(city, []);
            pricesByCity.get(city)!.push(price);
        }

        const district = districtFromListing(row);
        if (!district) {
            continue;
        }
        const key = city + "\0" + district;
        let bucket = districtMap.get(key);
        if (!bucket) {
            bucket = {city, district, listingCount: 0, soldCount: 0};
            districtMap.set(key, bucket);
        }
        bucket.listingCount += 1;
        if (row.proposalStatus === "SOLD") {
            bucket.soldCount += 1;
        }
    }

    const districts: DistrictAnalitics[] = Array.from(districtMap.values()).map((bucket) => {
        const demandScore = bucket.listingCount + bucket.soldCount;
        const liquidityScore = bucket.soldCount + bucket.soldCount / Math.max(bucket.listingCount, 1);
        return {
            city: bucket.city,
            district: bucket.district,
            listingCount: bucket.listingCount,
            soldCount: bucket.soldCount,
            demandScore,
            liquidityScore,
            demandRank: 0,
            liquidityRank: 0,
        };
    });

    rankWithinCity(districts, "demandScore", "demandRank");
    rankWithinCity(districts, "liquidityScore", "liquidityRank");
    districts.sort((a, b) => {
        const cityCmp = a.city.localeCompare(b.city);
        if (cityCmp !== 0) return cityCmp;
        return a.demandRank - b.demandRank || a.district.localeCompare(b.district);
    });

    const assetsByCity: CityAssetAnalitics[] = Array.from(kindsByCity.entries())
        .map(([city, kinds]) => ({city, kinds: Array.from(kinds).sort((a, b) => a.localeCompare(b))}))
        .sort((a, b) => a.city.localeCompare(b.city));

    const capitalByCity: CityCapitalAnalitics[] = Array.from(countByCity.keys())
        .map((city) => {
            const prices = (pricesByCity.get(city) || []).slice().sort((a, b) => a - b);
            return {
                city,
                listingCount: countByCity.get(city) || 0,
                pricedCount: prices.length,
                entryPrice: prices.length ? prices[0] : 0,
                typicalPrice: median(prices),
            };
        })
        .sort((a, b) => a.city.localeCompare(b.city));

    let currentCity: string | null = null;
    let currentCount = -1;
    for (const row of capitalByCity) {
        if (row.listingCount > currentCount) {
            currentCity = row.city;
            currentCount = row.listingCount;
        }
    }

    return {currentCity, districts, assetsByCity, capitalByCity};
}

export function formatUsdFromCents(cents: number): string {
    if (!isFinite(cents) || cents <= 0) {
        return "—";
    }
    return "$" + Math.round(cents / 100).toLocaleString("en-US");
}

export const ANALITICS_PROPOSAL_STATUSES: ProposalStatus[] = [
    "OWNER", "AGENT", "AUTO_COMPLETE", "AUTO_INCOMPLETE", "MANUAL", "NO_RESPONSE", "SOLD", "REJECTED",
];

function assetKindFromListing(listing: AnaliticsListing): string | null {
    const typeKey = listing.type ? String(listing.type) : "";
    const typeLabel = typeKey ? (getTypeLabel(typeKey) || typeKey) : "";
    const roomsLabel = listing.rooms != null && listing.rooms !== "" ? String(listing.rooms) : "";
    const head = typeLabel || roomsLabel;
    const reno = renovationLabel(listing.renovationStatus);
    if (!head && !reno) {
        return null;
    }
    if (head && reno) {
        return `${head} · ${reno}`;
    }
    return head || reno;
}

function renovationLabel(status?: string): string {
    switch (status) {
        case "RENOVATED":
            return "Renovated";
        case "RENOVATED_FURNISHED":
            return "Renovated furnished";
        case "NEEDS_RENOVATION":
            return "Needs renovation";
        default:
            return "";
    }
}

function rankWithinCity(
    rows: DistrictAnalitics[],
    scoreKey: "demandScore" | "liquidityScore",
    rankKey: "demandRank" | "liquidityRank",
) {
    const byCity = new Map<string, DistrictAnalitics[]>();
    for (const row of rows) {
        if (!byCity.has(row.city)) byCity.set(row.city, []);
        byCity.get(row.city)!.push(row);
    }
    byCity.forEach((group) => {
        group.sort((a, b) => {
            const scoreDiff = b[scoreKey] - a[scoreKey];
            if (scoreDiff !== 0) return scoreDiff;
            return a.district.localeCompare(b.district);
        });
        group.forEach((row, index) => {
            row[rankKey] = index + 1;
        });
    });
}

function median(sorted: number[]): number {
    if (!sorted.length) return 0;
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) {
        return sorted[mid];
    }
    return (sorted[mid - 1] + sorted[mid]) / 2;
}

function titleCase(value: string): string {
    return value.replace(/\b([a-z])/g, (ch) => ch.toUpperCase());
}

function fold(value: string): string {
    return value.toLowerCase().replace(/ё/g, "е");
}

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function districtsForCity(city: string): NamedDistrict[] {
    return NAMED_DISTRICTS.filter((row) => row.city === city);
}

function districtFromAddress(address: string | undefined, city: string): string | null {
    const raw = (address || "").trim();
    if (!raw) {
        return null;
    }
    const candidates = districtsForCity(city)
        .slice()
        .sort((a, b) => longestAlias(b) - longestAlias(a));
    for (const district of candidates) {
        if (district.aliases.some((alias) => containsToken(raw, alias))) {
            return district.name;
        }
    }
    return null;
}

function longestAlias(district: NamedDistrict): number {
    return district.aliases.reduce((max, alias) => Math.max(max, alias.length), 0);
}

function districtFromCoords(lat: number | undefined, lng: number | undefined, city: string): string | null {
    const la = Number(lat);
    const ln = Number(lng);
    if (!isFinite(la) || !isFinite(ln) || (la === 0 && ln === 0)) {
        return null;
    }
    let best: {name: string; dist: number} | null = null;
    for (const district of districtsForCity(city)) {
        const dist = haversineKm(la, ln, district.lat, district.lng);
        if (dist <= district.radiusKm && (!best || dist < best.dist)) {
            best = {name: district.name, dist};
        }
    }
    return best ? best.name : null;
}

function cityFromAddress(address?: string): string | null {
    if (!address) {
        return null;
    }
    const lower = address.toLowerCase();
    for (const place of KNOWN_PLACES) {
        if (place.cityTokens.some((token) => containsToken(lower, token))) {
            return place.label;
        }
    }
    for (const place of KNOWN_PLACES) {
        if (place.countryTokens.some((token) => containsToken(lower, token))) {
            return place.label;
        }
    }
    return null;
}

function cityFromCoords(lat?: number, lng?: number): string | null {
    const la = Number(lat);
    const ln = Number(lng);
    if (!isFinite(la) || !isFinite(ln) || (la === 0 && ln === 0)) {
        return null;
    }
    let best: {label: string; dist: number} | null = null;
    for (const place of KNOWN_PLACES) {
        const dist = haversineKm(la, ln, place.lat, place.lng);
        if (dist <= place.radiusKm && (!best || dist < best.dist)) {
            best = {label: place.label, dist};
        }
    }
    return best ? best.label : null;
}

function containsToken(haystack: string, token: string): boolean {
    const needle = fold(token).trim();
    if (!needle) {
        return false;
    }
    const source = fold(haystack);
    return new RegExp(
        "(^|[^\\p{L}\\p{N}])" + escapeRegex(needle) + "([^\\p{L}\\p{N}]|$)",
        "iu",
    ).test(source);
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const toRad = (deg: number) => deg * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
}
